/**
 * The label printer of the counter (T07-34, design/05 §6): price labels for card sleeves / boxes
 * and shelf labels. The label content (barcode, 税込 price, Rank A–D / 「PSA 10」, 「委託」) is made
 * by pos-service (`POST /pos/label-jobs` → ZPL / TSPL bytes or a PDF, T07-16) — the tablet only
 * moves the bytes to the printer, like receipts.
 *
 * | connection | printer | what the tablet does |
 * |---|---|---|
 * | `webusb` / `bluetooth` | TSPL (Xprinter XP-420B …) or ZPL (Zebra) label printer | raw bytes, its own USB device (never the receipt printer) |
 * | `network` | a LAN label printer behind `pos-print-bridge` | `POST /print?printer=labels` |
 * | `pdf` | an ordinary printer / A4 sheet labels | opens the PDF (the browser prints it) |
 * | `mock` | tests / no printer | keeps the jobs |
 */
import type { PosDatabase } from "../db";
import { readSetting, writeSetting } from "../db";
import { BridgePrinter } from "./bridge";
import { MockPrinter } from "./mock";
import { PrinterError, type PrinterDriver } from "./types";
import { WebBluetoothPrinter } from "./webbluetooth";
import { WebUsbPrinter, type UsbIdentity } from "./webusb";

export type LabelConnection = "webusb" | "bluetooth" | "network" | "pdf" | "mock" | "none";
export type LabelLanguage = "tspl" | "zpl";
export type LabelSizeSetting = "40x30" | "58x40";

export interface LabelPrinterSettings {
  connection: LabelConnection;
  language: LabelLanguage;
  size: LabelSizeSetting;
  bridgeUrl: string | null;
  bridgeToken: string | null;
  bridgePrinter: string | null;
  usbDevice?: UsbIdentity | null;
}

export const LABEL_SETTINGS_KEY = "labelPrinter";

export const DEFAULT_LABEL_SETTINGS: LabelPrinterSettings = {
  connection: "pdf",
  language: "tspl",
  size: "40x30",
  bridgeUrl: null,
  bridgeToken: null,
  bridgePrinter: "labels",
};

/** The admin's register config (`labelPrinter`) → this tablet's defaults. */
export function labelSettingsFromRegister(
  config: Record<string, unknown> | null | undefined,
  sizeDefault: string | undefined,
): LabelPrinterSettings {
  const connection = typeof config?.connection === "string" ? config.connection : "pdf";
  const mapped: LabelConnection =
    (["webusb", "bluetooth", "network", "mock", "none"] as const).find((c) => c === connection) ??
    (connection === "sunmi" ? "webusb" : "pdf");
  const language: LabelLanguage = config?.language === "zpl" ? "zpl" : "tspl";
  const address = typeof config?.address === "string" ? config.address : null;
  return {
    ...DEFAULT_LABEL_SETTINGS,
    connection: mapped,
    language,
    size: sizeDefault === "58x40" ? "58x40" : "40x30",
    bridgeUrl:
      mapped === "network" && address
        ? address.startsWith("http")
          ? address
          : `https://${address}`
        : null,
  };
}

export class LabelPrinter {
  private settings: LabelPrinterSettings = DEFAULT_LABEL_SETTINGS;
  private current: PrinterDriver | null = null;
  readonly mock = new MockPrinter();
  private readonly db: PosDatabase;
  private readonly listeners = new Set<() => void>();

  constructor(db: PosDatabase) {
    this.db = db;
  }

  async load(fallback?: LabelPrinterSettings): Promise<LabelPrinterSettings> {
    this.settings = await readSetting(
      this.db,
      LABEL_SETTINGS_KEY,
      fallback ?? DEFAULT_LABEL_SETTINGS,
    );
    this.current = null;
    this.emit();
    return this.settings;
  }

  async save(settings: LabelPrinterSettings): Promise<void> {
    this.settings = settings;
    this.current = null;
    await writeSetting(this.db, LABEL_SETTINGS_KEY, settings);
    this.emit();
  }

  get config(): LabelPrinterSettings {
    return this.settings;
  }

  /** The format pos-service renders for this printer (`pdf` when it prints through the browser). */
  get format(): "tspl" | "zpl" | "pdf" {
    return this.settings.connection === "pdf" || this.settings.connection === "none"
      ? "pdf"
      : this.settings.language;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    for (const listener of [...this.listeners]) {
      listener();
    }
  }

  private async rememberUsb(identity: UsbIdentity): Promise<void> {
    this.settings = { ...this.settings, usbDevice: identity };
    await writeSetting(this.db, LABEL_SETTINGS_KEY, this.settings);
    this.emit();
  }

  /** The driver for raw label bytes (not for `pdf` / `none`). */
  driver(): PrinterDriver {
    if (this.current) {
      return this.current;
    }
    const s = this.settings;
    if (s.connection === "webusb") {
      this.current = new WebUsbPrinter(undefined, {
        device: s.usbDevice ?? null,
        onChosen: (identity) => void this.rememberUsb(identity),
      });
    } else if (s.connection === "bluetooth") {
      this.current = new WebBluetoothPrinter();
    } else if (s.connection === "network") {
      this.current = new BridgePrinter({
        url: s.bridgeUrl ?? "",
        token: s.bridgeToken,
        printer: s.bridgePrinter,
      });
    } else if (s.connection === "mock") {
      this.current = this.mock;
    } else {
      throw new PrinterError(s.connection === "pdf" ? "not_supported" : "no_printer");
    }
    return this.current;
  }

  /** Send ZPL / TSPL bytes from pos-service to the label printer. */
  async print(bytes: Uint8Array): Promise<void> {
    await this.driver().print(bytes);
  }
}
